import React, { useCallback } from 'react';
import styled from 'styled-components';
import Button, { type ButtonPropsBase } from '@splunk/react-ui/Button';
import Tooltip from '@splunk/react-ui/Tooltip';
import { useActionButtonCallbacks } from '@splunk/dashboard-context';
import { actionMenuButtonClicked, useDispatch } from '@splunk/dashboard-state';
import { ActionItem } from '../ActionItem';

const StyledButton = styled(Button)`
    width: 28px;
    height: 28px;
`;

export interface ActionButtonProps extends ButtonPropsBase {
    title: string;
}

export const useActionButtonClick = <T extends unknown[]>(
    event?: string,
    onClick?: (...args: T) => void
) => {
    const dispatch = useDispatch();

    return useCallback(
        (...args: T) => {
            if (dispatch && event) {
                dispatch(
                    actionMenuButtonClicked({
                        event,
                    })
                );
            }
            onClick?.(...args);
        },
        [dispatch, event, onClick]
    );
};

export const ActionButton = ({
    disabled = false,
    title,
    ...rest
}: ActionButtonProps) => {
    const { onMouseEnter, onMouseLeave } = useActionButtonCallbacks();

    return (
        <ActionItem>
            <Tooltip
                contentRelationship="label"
                content={title}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                <StyledButton
                    appearance="subtle"
                    disabled={!!disabled}
                    data-disabled={!!disabled}
                    data-clickable
                    tabIndex={0}
                    {...rest}
                />
            </Tooltip>
        </ActionItem>
    );
};
